import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  ChevronRight,
  ChevronDown,
  Target,
  Award,
  UserPlus,
  UserMinus,
  ArrowRightLeft
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getLeagueDivisions,
  createLeagueDivision,
  updateLeagueDivision,
  deleteLeagueDivision,
  getLeagueTeams,
  updateLeagueTeam,
  getPlayers,
  getLeagues
} from '../services/api';
import { 
  Button, 
  Card, 
  CardHeader, 
  CardContent, 
  Modal, 
  ModalHeader, 
  ModalContent, 
  ModalFooter,
  FormDialog,
  Input,
  Select,
  SelectOption,
  Badge,
  StatusBadge,
  Loading,
  Spinner
} from './ui';

interface Division {
  id: number;
  name: string;
  teams: Team[];
  max_teams: number;
  league_id: number;
}

interface Team {
  id: number;
  name: string;
  captain_id: number;
  captain_name: string;
  members: TeamMember[];
  division_id: number;
}

interface TeamMember {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  sim_handicap: number;
  role: 'captain' | 'member';
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  sim_handicap: number;
  club: string;
}

interface LeagueDivisionManagerProps {
  leagueId: number;
}

const LeagueDivisionManager: React.FC<LeagueDivisionManagerProps> = ({ leagueId }) => {
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDivision, setExpandedDivision] = useState<number | null>(null);

  // Division editing state
  const [editingDivisionId, setEditingDivisionId] = useState<number | null>(null);
  const [editingDivisionName, setEditingDivisionName] = useState('');

  // Division creation form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [divisionForm, setDivisionForm] = useState({
    name: '',
    max_teams: 8
  });

  // Team creation form state
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState<number | null>(null);
  const [teamForm, setTeamForm] = useState({
    name: '',
    captain_id: 0
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (leagueId) {
      loadData();
    }
  }, [leagueId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load league settings first
      const leaguesResponse = await getLeagues();
      const league = leaguesResponse.data.find((l: any) => l.id === leagueId);
      const divisionsCount = league?.divisions_count || 2;
      const teamsPerDivision = league?.teams_per_division || 8;

      // Load divisions for this league
      const divisionsResponse = await getLeagueDivisions(leagueId);
      let divisionsData = divisionsResponse.data;

      // Auto-create divisions if they don't exist or if count doesn't match settings
      const currentDivisionCount = divisionsData.length;

      if (currentDivisionCount < divisionsCount) {
        const divisionNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
        const createdDivisions = [];

        // Create the missing divisions
        for (let i = currentDivisionCount; i < divisionsCount; i++) {
          try {
            const response = await createLeagueDivision(leagueId, {
              division_name: `Division ${divisionNames[i] || (i + 1)}`
            });
            createdDivisions.push(response.data);
          } catch (err) {
            console.error(`Error creating division ${i + 1}:`, err);
          }
        }

        // Merge existing and new divisions
        divisionsData = [...divisionsData, ...createdDivisions];

        if (createdDivisions.length > 0) {
          if (currentDivisionCount === 0) {
            toast.success(`Created ${createdDivisions.length} divisions based on league settings`);
          } else {
            toast.success(`Added ${createdDivisions.length} more division${createdDivisions.length > 1 ? 's' : ''} to match league settings`);
          }
        }
      } else if (currentDivisionCount > divisionsCount && divisionsCount > 0) {
        // More divisions exist than configured - show a warning
        toast.warning(`You have ${currentDivisionCount} divisions but settings specify ${divisionsCount}. You can manually delete extra divisions if needed.`);
      }

      // Load teams for this league
      const teamsResponse = await getLeagueTeams(leagueId);
      const teamsData = teamsResponse.data;

      // Load available users/players
      const usersResponse = await getPlayers();
      const usersData = usersResponse.data;

      // Group teams by division
      const divisionsWithTeams: Division[] = divisionsData.map((div: any) => ({
        id: div.id,
        name: div.division_name,
        max_teams: teamsPerDivision,
        league_id: div.league_id,
        teams: teamsData
          .filter((team: any) => team.division_id === div.id)
          .map((team: any) => ({
            id: team.id,
            name: team.name,
            captain_id: team.captain_id,
            captain_name: team.captain_name || 'Unknown',
            division_id: team.division_id,
            members: [] // Will be loaded separately if needed
          }))
      }));

      setDivisions(divisionsWithTeams);
      setAvailableUsers(usersData.map((user: any) => ({
        id: user.member_id,
        first_name: user.first_name,
        last_name: user.last_name,
        sim_handicap: user.sim_handicap || 0,
        club: user.club || 'Unknown'
      })));
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const validateDivisionForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!divisionForm.name.trim()) {
      errors.name = 'Division name is required';
    }

    if (divisionForm.max_teams < 4 || divisionForm.max_teams > 16) {
      errors.max_teams = 'Max teams must be between 4 and 16';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateTeamForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!teamForm.name.trim()) {
      errors.name = 'Team name is required';
    }

    if (!teamForm.captain_id) {
      errors.captain_id = 'Captain is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateDivision = async () => {
    if (!validateDivisionForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createLeagueDivision(leagueId, {
        division_name: divisionForm.name
      });

      const createdDivision: Division = {
        id: response.data.id,
        name: response.data.division_name,
        max_teams: divisionForm.max_teams,
        league_id: leagueId,
        teams: []
      };

      setDivisions(prev => [...prev, createdDivision]);
      setShowCreateForm(false);
      setDivisionForm({ name: '', max_teams: 8 });
      toast.success('Division created successfully');
    } catch (error) {
      console.error('Error creating division:', error);
      toast.error('Failed to create division');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!validateTeamForm() || !selectedDivision) {
      return;
    }

    setIsSubmitting(true);
    try {
      const captain = availableUsers.find(u => u.id === teamForm.captain_id);
      if (!captain) {
        toast.error('Selected captain not found');
        return;
      }

      const newTeam: Team = {
        id: Date.now(), // Temporary ID
        name: teamForm.name,
        captain_id: teamForm.captain_id,
        captain_name: `${captain.first_name} ${captain.last_name}`,
        division_id: selectedDivision,
        members: [
          {
            id: Date.now() + 1,
            user_id: captain.id,
            first_name: captain.first_name,
            last_name: captain.last_name,
            sim_handicap: captain.sim_handicap,
            role: 'captain'
          }
        ]
      };

      setDivisions(prev => 
        prev.map(division => 
          division.id === selectedDivision
            ? { ...division, teams: [...division.teams, newTeam] }
            : division
        )
      );

      setShowTeamForm(false);
      setTeamForm({ name: '', captain_id: 0 });
      setSelectedDivision(null);
      toast.success('Team created successfully');
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDivision = async (divisionId: number) => {
    if (!window.confirm('Are you sure you want to delete this division? This will also delete all teams in this division.')) {
      return;
    }

    try {
      await deleteLeagueDivision(leagueId, divisionId);
      setDivisions(prev => prev.filter(division => division.id !== divisionId));
      toast.success('Division deleted successfully');
    } catch (error: any) {
      console.error('Error deleting division:', error);
      toast.error(error.response?.data?.error || 'Failed to delete division');
    }
  };

  const handleStartEditDivision = (divisionId: number, currentName: string) => {
    setEditingDivisionId(divisionId);
    setEditingDivisionName(currentName);
  };

  const handleCancelEditDivision = () => {
    setEditingDivisionId(null);
    setEditingDivisionName('');
  };

  const handleSaveEditDivision = async (divisionId: number) => {
    if (!editingDivisionName.trim()) {
      toast.error('Division name cannot be empty');
      return;
    }

    try {
      const response = await updateLeagueDivision(leagueId, divisionId, {
        division_name: editingDivisionName.trim()
      });

      setDivisions(prev =>
        prev.map(division =>
          division.id === divisionId
            ? { ...division, name: response.data.division_name }
            : division
        )
      );

      setEditingDivisionId(null);
      setEditingDivisionName('');
      toast.success('Division name updated successfully');
    } catch (error: any) {
      console.error('Error updating division:', error);
      toast.error(error.response?.data?.error || 'Failed to update division');
    }
  };

  const handleMoveTeamToDivision = async (teamId: number, currentDivisionId: number, newDivisionId: number) => {
    try {
      // Don't update if division hasn't changed
      if (currentDivisionId === newDivisionId) return;

      const newDivision = divisions.find(d => d.id === newDivisionId);
      if (!newDivision) {
        toast.error('Selected division not found');
        return;
      }

      // Find the team in the current division
      const currentDivision = divisions.find(d => d.id === currentDivisionId);
      const team = currentDivision?.teams.find(t => t.id === teamId);
      if (!team) {
        toast.error('Team not found');
        return;
      }

      // Update team division via API
      await updateLeagueTeam(leagueId, teamId, { division_id: newDivisionId });

      // Update local state - move team from one division to another
      setDivisions(prev =>
        prev.map(division => {
          if (division.id === currentDivisionId) {
            // Remove team from current division
            return { ...division, teams: division.teams.filter(t => t.id !== teamId) };
          } else if (division.id === newDivisionId) {
            // Add team to new division with updated division_id
            return { ...division, teams: [...division.teams, { ...team, division_id: newDivisionId }] };
          }
          return division;
        })
      );

      toast.success(`Team moved to ${newDivision.name}`);
    } catch (error: any) {
      console.error('Error moving team:', error);
      toast.error(error.response?.data?.error || 'Failed to move team');
    }
  };

  const handleAddMemberToTeam = async (divisionId: number, teamId: number, userId: number) => {
    try {
      const user = availableUsers.find(u => u.id === userId);
      if (!user) return;

      const newMember: TeamMember = {
        id: Date.now(),
        user_id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        sim_handicap: user.sim_handicap,
        role: 'member'
      };

      setDivisions(prev =>
        prev.map(division =>
          division.id === divisionId
            ? {
                ...division,
                teams: division.teams.map(team => {
                  if (team.id === teamId) {
                    // Add new member and sort alphabetically (captain first)
                    const updatedMembers = [...team.members, newMember].sort((a, b) => {
                      // Captain always at the top
                      if (a.role === 'captain') return -1;
                      if (b.role === 'captain') return 1;

                      // Then sort alphabetically
                      const lastNameCompare = a.last_name.localeCompare(b.last_name);
                      if (lastNameCompare !== 0) return lastNameCompare;
                      return a.first_name.localeCompare(b.first_name);
                    });
                    return { ...team, members: updatedMembers };
                  }
                  return team;
                })
              }
            : division
        )
      );
      toast.success('Member added to team');
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error('Failed to add member');
    }
  };

  const handleRemoveMemberFromTeam = async (divisionId: number, teamId: number, memberId: number) => {
    try {
      setDivisions(prev => 
        prev.map(division => 
          division.id === divisionId
            ? {
                ...division,
                teams: division.teams.map(team =>
                  team.id === teamId
                    ? { ...team, members: team.members.filter(member => member.id !== memberId) }
                    : team
                )
              }
            : division
        )
      );
      toast.success('Member removed from team');
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  const getAvailableUsersForTeam = (team: Team) => {
    // Get all users who are already on ANY team across ALL divisions
    const allTeamMemberIds = new Set<number>();
    divisions.forEach(division => {
      division.teams.forEach(t => {
        t.members.forEach(member => {
          allTeamMemberIds.add(member.user_id);
        });
      });
    });

    // Filter out users who are already on any team
    const users = availableUsers.filter(user => !allTeamMemberIds.has(user.id));

    // Sort alphabetically by last name, then first name
    return users.sort((a, b) => {
      const lastNameCompare = a.last_name.localeCompare(b.last_name);
      if (lastNameCompare !== 0) return lastNameCompare;
      return a.first_name.localeCompare(b.first_name);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" label="Loading divisions..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Users className="w-8 h-8 text-brand-neon-green" />
          <div>
            <h2 className="text-2xl font-bold text-brand-black">Division Management</h2>
            <p className="text-neutral-600">Create and manage league divisions</p>
          </div>
        </div>
        
        <Button
          onClick={() => setShowCreateForm(true)}
          icon={Plus}
          variant="primary"
        >
          Create Division
        </Button>
      </div>

      {/* Divisions List */}
      <div className="space-y-4">
        {divisions.map((division) => (
          <Card key={division.id}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setExpandedDivision(expandedDivision === division.id ? null : division.id)}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  {expandedDivision === division.id ? (
                    <ChevronDown className="w-5 h-5 text-neutral-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-neutral-500" />
                  )}
                </button>

                <div className="flex-1">
                  {editingDivisionId === division.id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={editingDivisionName}
                        onChange={(e) => setEditingDivisionName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveEditDivision(division.id);
                          } else if (e.key === 'Escape') {
                            handleCancelEditDivision();
                          }
                        }}
                        className="px-3 py-1.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-neon-green focus:border-transparent"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveEditDivision(division.id)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Save"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancelEditDivision}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <div>
                        <h3 className="text-lg font-semibold text-brand-black">{division.name}</h3>
                        <p className="text-sm text-neutral-600">
                          {division.teams.length} / {division.max_teams} teams
                        </p>
                      </div>
                      <button
                        onClick={() => handleStartEditDivision(division.id, division.name)}
                        className="p-1.5 text-neutral-500 hover:text-brand-neon-green hover:bg-neutral-100 rounded-lg transition-colors"
                        title="Edit division name"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => {
                    setSelectedDivision(division.id);
                    setShowTeamForm(true);
                  }}
                  disabled={division.teams.length >= division.max_teams}
                  icon={Plus}
                  variant="secondary"
                  size="sm"
                >
                  Add Team
                </Button>

                <Button
                  variant="danger"
                  size="sm"
                  icon={Trash2}
                  onClick={() => handleDeleteDivision(division.id)}
                >
                  Delete
                </Button>
              </div>
            </div>
              
              {expandedDivision === division.id && (
                <div className="mt-6 space-y-4">
                  {division.teams.length === 0 ? (
                    <div className="text-center py-8 text-neutral-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
                      <p>No teams in this division yet</p>
                      <p className="text-sm">Click "Add Team" to create the first team</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {division.teams.map((team) => (
                        <Card key={team.id} variant="outlined">
                          <div className="mb-3">
                            <h4 className="font-semibold text-brand-black mb-2">{team.name}</h4>
                            <div className="flex items-center space-x-2">
                              <ArrowRightLeft className="w-4 h-4 text-neutral-500" />
                              <span className="text-sm text-neutral-600">Move to:</span>
                              <select
                                value={division.id}
                                onChange={(e) => handleMoveTeamToDivision(team.id, division.id, parseInt(e.target.value))}
                                className="flex-1 px-2 py-1 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-neon-green focus:border-transparent text-sm bg-white"
                              >
                                {divisions.map((div) => (
                                  <option key={div.id} value={div.id}>
                                    {div.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <p className="text-sm text-neutral-600">Captain: {team.captain_name}</p>
                            <p className="text-sm text-neutral-600">
                              Members: {team.members.length}
                            </p>
                            
                            <div className="space-y-1">
                              {team.members.map((member) => (
                                <div key={member.id} className="flex items-center text-sm">
                                  <span className="text-neutral-700">
                                    {member.first_name} {member.last_name}
                                    {member.role === 'captain' && (
                                      <Badge variant="brand" size="sm" className="ml-2">
                                        Captain
                                      </Badge>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>

      {/* Create Division Modal */}
      <FormDialog
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={(e) => { e.preventDefault(); handleCreateDivision(); }}
        title="Create Division"
        submitText="Create Division"
        loading={isSubmitting}
      >
        <div className="space-y-4">
          <Input
            label="Division Name"
            value={divisionForm.name}
            onChange={(e) => setDivisionForm(prev => ({ ...prev, name: e.target.value }))}
            error={formErrors.name}
            required
            placeholder="e.g., Division A"
          />

          <Input
            label="Max Teams"
            type="number"
            min="4"
            max="16"
            value={divisionForm.max_teams}
            onChange={(e) => setDivisionForm(prev => ({ ...prev, max_teams: parseInt(e.target.value) }))}
            error={formErrors.max_teams}
            required
          />
        </div>
      </FormDialog>

      {/* Create Team Modal */}
      <FormDialog
        open={showTeamForm}
        onClose={() => {
          setShowTeamForm(false);
          setSelectedDivision(null);
        }}
        onSubmit={(e) => { e.preventDefault(); handleCreateTeam(); }}
        title="Create Team"
        submitText="Create Team"
        loading={isSubmitting}
      >
        <div className="space-y-4">
          <Input
            label="Team Name"
            value={teamForm.name}
            onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
            error={formErrors.name}
            required
            placeholder="e.g., Team Alpha"
          />

          <Select
            label="Captain"
            value={teamForm.captain_id}
            onChange={(e) => setTeamForm(prev => ({ ...prev, captain_id: parseInt(e.target.value) }))}
            options={[
              { value: 0, label: 'Select captain...' },
              ...availableUsers.map((user) => ({
                value: user.id,
                label: `${user.first_name} ${user.last_name} (HCP: ${user.sim_handicap})`
              }))
            ]}
            error={formErrors.captain_id}
            required
          />
        </div>
      </FormDialog>
    </div>
  );
};

export default LeagueDivisionManager;
